import moment from 'moment';
import { Geschlecht } from '@/types/Geschlecht';
import { PatientStatus } from '@/types/PatientStatus';

export default class Patient {
    /** PK: Patient | erforderlich */
    public patnr: number | null = null;
    /** eindeutiger Personenschlüssel aus zentralen Personenindex (Z-PI) | max. 255 Zeichen */
    public bpk_gh: string | null = null;
    /** j=ja, n=nein, u=unklar | max. 1 Zeichen */
    public elga_gefunden: PatientStatus | null = null;
    /**  */
    public pat_kontakt: string | null = null;
    /**  | max. 50 Zeichen | erforderlich */
    public nachname: string | null = '';
    /**  | max. 50 Zeichen */
    public vorname: string | null = '';
    /**  | max. 50 Zeichen */
    public titel: string | null = '';
    /**  | max. 50 Zeichen */
    public titel_nach_name: string | null = null;
    /**  */
    public geburtsdatum: string | null = null;
    /** M=männlich; W=weiblich; U=unbekannt (ELGA: M=Mann; F=Frau; UNK=unbekannt) | max. 1 Zeichen */
    public geschlecht: string | null = null;
    /** 10 Ziffern (Nummer 4-stellig und Geburtsdatum 6-stellig im Format "ttmmjj") | max. 10 Zeichen */
    public svnr: string | null = null;
    /**  | max. 50 Zeichen */
    public strasse: string | null = null;
    /**  | max. 8 Zeichen */
    public plz: string | null = null;
    /**  | max. 50 Zeichen */
    public ort: string | null = null;
    /** FK: Land | max. 3 Zeichen */
    public land: string | null = 'AUT';
    /**  | max. 255 Zeichen */
    public email: string | null = null;
    /**  | max. 50 Zeichen */
    public telefon: string | null = null;
    /**  | max. 50 Zeichen */
    public telefon2: string | null = null;
    /**  | max. 100 Zeichen */
    public firma: string | null = null;
    /** FK: Versicherung */
    public versicherung_id: number | null = null;
    /**  | max. 1 Zeichen */
    public wahlarzt: string | null = null;
    /** Standardarzt in Gruppenpraxis (Datenübernahme) | max. 255 Zeichen */
    public arzt: string | null = null;
    /** FK: Person Standardarzt in Gruppenpraxis */
    public person_id_arzt: number | null = null;
    /** FK: Risikogruppe Verweis auf Tab. eImpf_SpecialSituationIndication_VS | max. 10 Zeichen */
    public risikogruppe_code: string | null = null;
    /** FK: Erkrankungscode Verweis auf Tab. eImpf_ImpfrelevanteErkrankung_VS | max. 10 Zeichen */
    public erkrankung_code: string | null = null;
    /**  */
    public erkrankungsdatum: string | null = null;
    /**  | max. 100 Zeichen */
    public erkrankungsbemerkung: string | null = null;
    /**  | max. 1073741823 Zeichen */
    public anmerkung: string | null = null;
    /** aus Cave übernommen | max. 1073741823 Zeichen */
    public anamnese: string | null = null;
    /**  | max. 1073741823 Zeichen */
    public diagnose: string | null = null;
    public diagnose_text: string | null = null;
    /**  | max. 1073741823 Zeichen */
    public impfreaktionen: string | null = null;
    /**  | max. 1073741823 Zeichen */
    public dekurs: string | null = null;
    /**  */
    public koerpergroesse: number | null = null;
    /**  */
    public koerpergewicht: number | null = null;
    /**  */
    public restbetrag: number | null = null;
    /**  | max. 50 Zeichen */
    public rech_adrzeile1: string | null = null;
    /**  | max. 50 Zeichen */
    public rech_adrzeile2: string | null = null;
    /**  | max. 50 Zeichen */
    public rech_adrzeile3: string | null = null;
    /**  | max. 50 Zeichen */
    public rech_strasse: string | null = null;
    /**  | max. 8 Zeichen */
    public rech_plz: string | null = null;
    /**  | max. 50 Zeichen */
    public rech_ort: string | null = null;
    /**  | max. 3 Zeichen */
    public rech_land: string | null = null;
    /** Patient aktiv j/n | max. 1 Zeichen */
    public aktiv: string | null = 'j';
    /** FK: Impfstelle Verweis auf Tab. Impfstellen */
    public impfstelle_id: number | null = null;
    /**  */
    public zeitstempel: string | null = null;
    /**  | max. 1 Zeichen */
    public dsgvo_einverstaendnis: string | null = 'j';
    /**  */
    public dsgvo_datum: string | null = null;
    /**  | max. 50 Zeichen */
    public dsgvo_bemerkung: string | null = null;
    /** Patient hat einen Folgetermin j/n | max. 1 Zeichen */
    public hat_folgetermin: string | null = null;

    public relerk_displayname: string | null = null;
    public ssi_displayname: string | null = null;
    public land_bezeichnung: string | null = null;
    public rech_land_bezeichnung: string | null = null;
    public patientengruppe_id: number | null = null;
    public patientengruppe_name: string | null = null;
    public dienstort_id: number | null = null;
    public risikopatient: string | null = 'n';
    public studien_zustimmung: string | null = null;
    public studien_zustimmung_datum: string | null = null;
    public kontakt_zustimmung: string | null = null;
    public kontakt_zustimmung_datum: string | null = null;
    public versicherung_name: string | null = null;

    private composeFullName(formatted: boolean): string {
        const boldStartTag = formatted ? '<b>' : '';
        const boldEndTag = formatted ? '</b>' : '';
        let name = this.vorname ?? '';
        if (this.nachname) {
            name += ' ' + boldStartTag + this.nachname + boldEndTag;
        }
        if (this.titel) {
            name = this.titel + ' ' + name;
        }
        if (this.titel_nach_name) {
            name = name + ', ' + this.titel_nach_name;
        }
        return name;
    }

    public get name(): string {
        return this.composeFullName(false);
    }

    public get nameFormatted(): string {
        return this.composeFullName(true);
    }

    public get nachnameVornameTitel(): string {
        let name = this.nachname ?? '';
        if (this.vorname) {
            name += ' ' + this.vorname;
        }
        if (this.titel) {
            name += ' ' + this.titel;
        }
        if (this.titel_nach_name) {
            name += ' ' + this.titel_nach_name;
        }
        return name;
    }

    public get nameGebdat(): string {
        let name = this.vorname ?? '';
        if (this.nachname) {
            name += ' ' + this.nachname;
        }
        if (this.titel) {
            name = this.titel + ' ' + name;
        }
        return name + ' ' + this.Geburtsdatum;
    }

    public get Geschlecht(): string {
        switch (this.geschlecht) {
            case Geschlecht.Maennlich:
                return 'männlich';
            case Geschlecht.Weiblich:
                return 'weiblich';
            case Geschlecht.Divers:
                return 'divers';
            default:
                return '-';
        }
    }

    public get Adresse(): string {
        let adresse = this.AdresseOhneLand;

        if (this.land_bezeichnung) {
            if (adresse !== '') {
                adresse += ' - ' + this.land_bezeichnung;
            } else {
                adresse += this.land_bezeichnung;
            }
        }

        return adresse;
    }

    public get AdresseOhneLand(): string {
        const adresse = [];

        if (this.strasse) {
            if (this.plz || this.ort) {
                adresse.push(this.strasse + ',');
            } else {
                adresse.push(this.strasse);
            }
        }

        if (this.plz) {
            adresse.push(this.plz);
        }

        if (this.ort) {
            adresse.push(this.ort);
        }

        return adresse.join(' ');
    }

    public get Geburtsdatum(): string {
        const date = this.geburtsdatum?.split(' ')[0]?.split('-') || [];
        return date.length === 3 ? date[2] + '.' + date[1] + '.' + date[0] : '-';
    }

    public get alter(): number | string {
        if (this.geburtsdatum) {
            return moment().diff(moment(this.geburtsdatum), 'years');
        }
        return '-';
    }

    public get Svnr(): string {
        if (this.svnr?.length === 4) {
            if (this.geburtsdatum) {
                const d = moment(this.geburtsdatum);
                return (d.isValid()) ? (this.svnr || '----') + ' ' + d.format('DDMMYY') : '-';
            }
        }
        return this.svnr ?? '-';
    }

    public get hasValidSvnr(): boolean {
        return this.svnr !== null &&
            this.svnr.length === 10 &&
            !this.svnr.startsWith('0000') &&
            !this.svnr.endsWith('000000');
    }

    constructor(data?: Partial<Patient>) {
        Object.assign(this, data);
    }

    public clone(): Patient {
        return new Patient(this);
    }

    public hasSameNachnameAs(patient: Patient): boolean {
        return patient.nachname?.toLocaleUpperCase()?.trim() === this.nachname?.toLocaleUpperCase()?.trim();
    }

    public hasSameVornameAs(patient: Patient): boolean {
        return patient.vorname?.toLocaleUpperCase()?.trim() === this.vorname?.toLocaleUpperCase()?.trim();
    }

    public hasSameGeburtsdatumAs(patient: Patient): boolean {
        return patient.geburtsdatum === this.geburtsdatum;
    }

    public hasSameSvnrAs(patient: Patient): boolean {
        return patient.svnr === this.svnr;
    }
}
